import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

const emptyForm = {
  trainer_id: "",
  week_start: "",
  week_end: "",
  is_general_trainer: false,
  active_clients: "",
  referrals: "",
  transformations: "",
  asked_for_google_review: false,
  asked_for_video_shoot: false,
  remarks: "",
};

const NutritionReferrals = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [trainers, setTrainers] = useState([]);

  // filters
  const [filterWeekStart, setFilterWeekStart] = useState("");
  const [filterWeekEnd, setFilterWeekEnd] = useState("");

  // modal
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // export
  const [exporting, setExporting] = useState(false);

  const { state } = useContext(NoteState);
  const { branch_id, UId, role } = state;

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterWeekStart, filterWeekEnd]);

  useEffect(() => {
    fetchTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Data fetchers ──────────────────────────────────────────────────────────
  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await UserServices.getNutritionReferrals({
        branch_id,
        nutritionist_id: role === "10" ? UId : undefined,
        week_start: filterWeekStart || undefined,
        week_end: filterWeekEnd || undefined,
      });
      if (res.status === 200 && res.data.status) {
        setRecords(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const res = await UserServices.getNutritionReferralTrainers(branch_id);
      if (res.status === 200 && res.data.status) {
        setTrainers(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // ── Group records by week period ───────────────────────────────────────────
  const groupedRecords = records.reduce((acc, rec) => {
    const key = `${rec.week_start}_${rec.week_end}`;
    if (!acc[key]) acc[key] = { week_start: rec.week_start, week_end: rec.week_end, items: [] };
    acc[key].items.push(rec);
    return acc;
  }, {});

  const sortedWeeks = Object.values(groupedRecords).sort((a, b) =>
    b.week_start > a.week_start ? 1 : -1
  );

  const formatWeek = (start, end) => {
    const s = moment(start).format("Do MMM, YYYY");
    const e = moment(end).format("Do MMM, YYYY");
    return `${s} – ${e}`;
  };

  // ── Modal helpers ──────────────────────────────────────────────────────────
  const handleOpenAdd = (prefillWeek = null) => {
    setEditMode(false);
    setSelectedId(null);
    setFormData({
      ...emptyForm,
      week_start: prefillWeek?.week_start
        ? moment(prefillWeek.week_start).format("YYYY-MM-DD")
        : "",
      week_end: prefillWeek?.week_end
        ? moment(prefillWeek.week_end).format("YYYY-MM-DD")
        : "",
    });
    setShowModal(true);
  };

  const handleOpenEdit = (rec) => {
    setEditMode(true);
    setSelectedId(rec.id);
    setFormData({
      trainer_id:              rec.trainer_id               ?? "",
      week_start:              rec.week_start
        ? moment(rec.week_start).format("YYYY-MM-DD")
        : "",
      week_end:                rec.week_end
        ? moment(rec.week_end).format("YYYY-MM-DD")
        : "",
      is_general_trainer:      !!rec.is_general_trainer,
      active_clients:          rec.active_clients           ?? "",
      referrals:               rec.referrals                ?? "",
      transformations:         rec.transformations          ?? "",
      asked_for_google_review: !!rec.asked_for_google_review,
      asked_for_video_shoot:   !!rec.asked_for_video_shoot,
      remarks:                 rec.remarks                  ?? "",
    });
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setFormData({ ...emptyForm });
  };

  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const newVal = type === "checkbox" ? checked : value;

    setFormData((prev) => ({ ...prev, [name]: newVal }));

    // Auto-fill active clients when a specific trainer is selected
    if (name === "trainer_id" && value && !formData.is_general_trainer) {
      try {
        const res = await UserServices.getNutritionTrainerActiveClients(value, branch_id);
        if (res.status === 200 && res.data.status) {
          setFormData((prev) => ({ ...prev, active_clients: res.data.active_clients }));
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.week_start || !formData.week_end) {
      alert("Please set week start and end dates.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        branch_id,
        nutritionist_id:         UId,
        trainer_id:              formData.trainer_id   || null,
        week_start:              formData.week_start,
        week_end:                formData.week_end,
        is_general_trainer:      formData.is_general_trainer,
        active_clients:          formData.is_general_trainer ? null : (formData.active_clients !== "" ? formData.active_clients : null),
        referrals:               formData.referrals !== "" ? formData.referrals : 0,
        transformations:         formData.is_general_trainer ? null : (formData.transformations !== "" ? formData.transformations : null),
        asked_for_google_review: !formData.is_general_trainer && formData.asked_for_google_review,
        asked_for_video_shoot:   !formData.is_general_trainer && formData.asked_for_video_shoot,
        remarks:                 formData.remarks || null,
      };

      if (editMode) {
        await UserServices.updateNutritionReferral(selectedId, payload);
      } else {
        await UserServices.createNutritionReferral(payload);
      }

      handleClose();
      fetchRecords();
    } catch (err) {
      console.log(err);
      alert("Error saving record.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await UserServices.deleteNutritionReferral(id);
      fetchRecords();
    } catch (err) {
      console.log(err);
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExcelExport = async () => {
    setExporting(true);
    try {
      const response = await UserServices.exportNutritionReferrals({
        branch_id,
        nutritionist_id: role === "10" ? UId : undefined,
        week_start: filterWeekStart || undefined,
        week_end:   filterWeekEnd   || undefined,
      });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", `Referral_Sheet_${moment().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("Error exporting referral sheet.");
    } finally {
      setExporting(false);
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfExport = () => {
    setPdfLoading(true);
    try {
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Referral Sheet Report", 14, 18);
      doc.setFontSize(10);
      doc.text(`Generated: ${moment().format("DD MMM YYYY")}`, 14, 26);
      doc.autoTable({
        startY: 32,
        head: [["S#", "Week Period", "Trainer", "Type", "Active Clients", "Referrals", "Transformations", "Google Review", "Video Shoot", "Remarks"]],
        body: records.map((r, i) => [
          i + 1,
          r.week_start && r.week_end
            ? `${moment(r.week_start).format("DD MMM")} – ${moment(r.week_end).format("DD MMM YYYY")}`
            : "",
          r.trainer ? `${r.trainer.first_name} ${r.trainer.last_name}` : "N/A",
          r.is_general_trainer ? "General Trainer" : "Trainer",
          r.is_general_trainer ? "-" : (r.active_clients ?? 0),
          r.referrals ?? 0,
          r.is_general_trainer ? "-" : (r.transformations ?? 0),
          r.is_general_trainer ? "-" : (r.asked_for_google_review ? "Yes" : "No"),
          r.is_general_trainer ? "-" : (r.asked_for_video_shoot ? "Yes" : "No"),
          r.remarks || "",
        ]),
        headStyles: { fillColor: [26, 92, 69], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 248, 244] },
        styles: { fontSize: 7 },
      });
      doc.save(`Referral_Sheet_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Error generating PDF.");
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        {/* Header */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="page-heading">
              <h3>
                <i className="bi bi-file-earmark-person me-2"></i>Referral Sheet
              </h3>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body py-2">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <label className="form-label small mb-0 text-muted">Week Start</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filterWeekStart}
                  onChange={(e) => setFilterWeekStart(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label small mb-0 text-muted">Week End</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filterWeekEnd}
                  onChange={(e) => setFilterWeekEnd(e.target.value)}
                />
              </div>
              {(filterWeekStart || filterWeekEnd) && (
                <div className="col-auto align-self-end">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setFilterWeekStart(""); setFilterWeekEnd(""); }}
                  >
                    <i className="bi bi-x me-1"></i>Clear
                  </button>
                </div>
              )}
              <div className="col-auto ms-auto align-self-end d-flex gap-2">
                <button
                  className="btn btn-success btn-sm"
                  onClick={handleExcelExport}
                  disabled={exporting || pdfLoading}
                  title="Download Excel"
                >
                  <i className="bi bi-file-earmark-excel me-1"></i>
                  {exporting ? "Exporting..." : "Excel"}
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handlePdfExport}
                  disabled={exporting || pdfLoading}
                  title="Download PDF"
                >
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                  {pdfLoading ? "Generating..." : "PDF"}
                </button>
                <button className="btn btn-primary btn-sm" onClick={() => handleOpenAdd()}>
                  <i className="bi bi-plus-circle me-1"></i>Add Record
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : sortedWeeks.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center text-muted py-5">
              <i className="bi bi-clipboard-x fs-2 mb-2 d-block"></i>
              No referral records found.
            </div>
          </div>
        ) : (
          sortedWeeks.map((week) => {
            const normalRows   = week.items.filter((r) => !r.is_general_trainer);
            const generalRows  = week.items.filter((r) =>  r.is_general_trainer);

            return (
              <div key={`${week.week_start}_${week.week_end}`} className="mb-4">
                {/* Week Period Header */}
                <div
                  className="d-flex justify-content-between align-items-center px-3 py-2 rounded-top"
                  style={{ background: "#1a5c45", color: "#fff" }}
                >
                  <span className="fw-semibold">
                    <i className="bi bi-calendar-week me-2"></i>
                    {formatWeek(week.week_start, week.week_end)}
                  </span>
                  <button
                    className="btn btn-sm btn-light"
                    onClick={() => handleOpenAdd({ week_start: week.week_start, week_end: week.week_end })}
                  >
                    <i className="bi bi-plus-circle me-1"></i>Add to this week
                  </button>
                </div>

                <div className="card border-0 shadow-sm" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                          <tr>
                            <th className="text-center" style={{ width: 50 }}>S#</th>
                            <th>Trainer's Name</th>
                            <th className="text-center">Active Clients</th>
                            <th className="text-center">Referrals</th>
                            <th className="text-center">Transformations</th>
                            <th className="text-center">Google Review</th>
                            <th className="text-center">Video Shoot</th>
                            <th>Remarks</th>
                            <th className="text-center" style={{ width: 90 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Normal Trainers */}
                          {normalRows.map((rec, idx) => (
                            <tr key={rec.id}>
                              <td className="text-center text-muted">{idx + 1}</td>
                              <td className="fw-semibold">
                                {rec.trainer
                                  ? `${rec.trainer.first_name} ${rec.trainer.last_name}`
                                  : <span className="text-muted fst-italic">—</span>}
                              </td>
                              <td className="text-center">{rec.active_clients ?? 0}</td>
                              <td className="text-center fw-semibold text-primary">{rec.referrals ?? 0}</td>
                              <td className="text-center">{rec.transformations ?? 0}</td>
                              <td className="text-center">
                                {rec.asked_for_google_review
                                  ? <i className="bi bi-check-square-fill text-success fs-5"></i>
                                  : <i className="bi bi-square text-secondary fs-5"></i>}
                              </td>
                              <td className="text-center">
                                {rec.asked_for_video_shoot
                                  ? <i className="bi bi-check-square-fill text-success fs-5"></i>
                                  : <i className="bi bi-square text-secondary fs-5"></i>}
                              </td>
                              <td className="text-muted small">{rec.remarks || "—"}</td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() => handleOpenEdit(rec)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(rec.id)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}

                          {/* General Trainers sub-heading */}
                          {generalRows.length > 0 && (
                            <tr style={{ background: "#f5c518" }}>
                              <td colSpan={9} className="fw-bold text-dark py-1 ps-3">
                                <i className="bi bi-people-fill me-2"></i>General Trainers
                              </td>
                            </tr>
                          )}

                          {/* General Trainer Rows */}
                          {generalRows.map((rec, idx) => (
                            <tr key={rec.id} style={{ background: "#fffde7" }}>
                              <td className="text-center text-muted">{normalRows.length + idx + 1}</td>
                              <td className="fw-semibold">
                                {rec.trainer
                                  ? `${rec.trainer.first_name} ${rec.trainer.last_name}`
                                  : <span className="text-muted fst-italic">—</span>}
                              </td>
                              <td className="text-center text-muted">–</td>
                              <td className="text-center fw-semibold text-primary">{rec.referrals ?? 0}</td>
                              <td className="text-center text-muted">–</td>
                              <td className="text-center text-muted">–</td>
                              <td className="text-center text-muted">–</td>
                              <td className="text-muted small">{rec.remarks || "—"}</td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  onClick={() => handleOpenEdit(rec)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(rec.id)}
                                  title="Delete"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}

                          {week.items.length === 0 && (
                            <tr>
                              <td colSpan={9} className="text-center text-muted py-3">
                                No records for this week.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* ── Add / Edit Modal ── */}
        <Modal show={showModal} onHide={handleClose} size="lg" backdrop="static">
          <Modal.Header closeButton style={{ background: "#1a5c45", color: "#fff" }}>
            <Modal.Title>
              <i className="bi bi-file-earmark-person me-2"></i>
              {editMode ? "Edit Referral Record" : "Add Referral Record"}
            </Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmit}>
            <Modal.Body>
              <div className="row g-3">

                {/* Week Start / End */}
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Week Start <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    name="week_start"
                    value={formData.week_start}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Week End <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    name="week_end"
                    value={formData.week_end}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Trainer */}
                <div className="col-md-8">
                  <label className="form-label fw-semibold">Trainer</label>
                  <select
                    className="form-select"
                    name="trainer_id"
                    value={formData.trainer_id}
                    onChange={handleChange}
                  >
                    <option value="">— Select Trainer —</option>
                    {trainers.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>

                {/* General Trainer Toggle */}
                <div className="col-md-4 d-flex align-items-end pb-1">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isGeneralTrainer"
                      name="is_general_trainer"
                      checked={formData.is_general_trainer}
                      onChange={handleChange}
                    />
                    <label className="form-check-label fw-semibold" htmlFor="isGeneralTrainer">
                      General Trainer
                    </label>
                  </div>
                </div>

                {/* Referrals */}
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Referrals</label>
                  <input
                    type="number"
                    className="form-control"
                    name="referrals"
                    min="0"
                    placeholder="0"
                    value={formData.referrals}
                    onChange={handleChange}
                  />
                </div>

                {/* Fields hidden for General Trainers */}
                {!formData.is_general_trainer && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Active Clients</label>
                      <input
                        type="number"
                        className="form-control bg-light"
                        name="active_clients"
                        min="0"
                        placeholder="0"
                        value={formData.active_clients}
                        readOnly
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Transformations</label>
                      <input
                        type="number"
                        className="form-control"
                        name="transformations"
                        min="0"
                        placeholder="0"
                        value={formData.transformations}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="googleReview"
                          name="asked_for_google_review"
                          checked={formData.asked_for_google_review}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="googleReview">
                          Asked for Google Review
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="videoShoot"
                          name="asked_for_video_shoot"
                          checked={formData.asked_for_video_shoot}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="videoShoot">
                          Asked for Video Shoot
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {/* Remarks */}
                <div className="col-12">
                  <label className="form-label fw-semibold">Remarks</label>
                  <textarea
                    className="form-control"
                    name="remarks"
                    rows={2}
                    placeholder="Optional remarks..."
                    value={formData.remarks}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : editMode ? "Update" : "Save"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default NutritionReferrals;
